import { useEffect, useRef, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  useDynamicFormsFetch,
  useGlobalEffectActions,
  usePageContext,
  useRidesActions,
} from "@ryde/redux";
import {
  isEntityClientSideWithServerDatesFiltered,
  isEntityServerSideFiltered,
  isRideEntity,
  isTranslatableItem,
  Log,
  shouldRefetchGridOnStarredDisplayModeChange,
  starredDisplayModeAtomFamily,
  StarredDisplayModeEnum,
  starredRowsCountSF,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { getNextStarredDisplayModeForCycle } from "./starred-utils";

export type TUseStarredButtonContextProvider = ReturnType<
  typeof useStarredButtonContextProvider
>;

export const useStarredButtonContextProvider = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { fetchTableRowsAndGroupValues } = useDynamicFormsFetch(entity);
  const { fetchRides } = useRidesActions();
  const [isDataReloading, startTransition] = useTransition();
  const [starredDisplayMode, setStarredDisplayMode] = useRecoilState(
    starredDisplayModeAtomFamily(entity),
  );

  const previousDisplayModeRef = useRef(starredDisplayMode);
  const { resetPaginationAppEvent } = useGlobalEffectActions();
  const starredCount = useRecoilValue(starredRowsCountSF(entity));

  const togglePopover = useEvent((): void => {
    setIsPopoverOpen(prevOpen => !prevOpen);
  });

  const closePopover = useEvent(
    (event?: React.MouseEvent | React.TouchEvent): void => {
      if (anchorRef.current?.contains(event?.target as HTMLElement)) {
        return;
      }

      setIsPopoverOpen(false);
    },
  );

  const handleItemClick = useEvent(
    (
      event: React.MouseEvent | React.TouchEvent,
      value: StarredDisplayModeEnum,
    ) => {
      setStarredDisplayMode(value);

      closePopover(event);
    },
  );
  const handleButtonClick = useEvent(() => {
    resetPaginationAppEvent();

    setStarredDisplayMode(prev => getNextStarredDisplayModeForCycle(prev));
  });

  const startServerFetch = useEvent(() => {
    startTransition(() => {
      if (isRideEntity(entity)) {
        fetchRides()
          .then(result => {
            if (!result.success && isTranslatableItem(result.error)) {
              warnAndToastSimpleResult(result, t);
            }
          })
          .catch(error => Log.warn("Error fetching rides", error));
      } else {
        // Starred ids narrow a server-paged query, so a grouped grid refetches its groups and counts too.
        fetchTableRowsAndGroupValues();
      }
    });
  });

  useEffect(() => {
    const isServerFetchedEntity =
      isEntityServerSideFiltered(entity) ||
      isEntityClientSideWithServerDatesFiltered(entity);

    if (!isServerFetchedEntity) {
      return;
    }

    const previousDisplayMode = previousDisplayModeRef.current;
    previousDisplayModeRef.current = starredDisplayMode;

    if (
      !shouldRefetchGridOnStarredDisplayModeChange({
        entity,
        previousDisplayMode,
        nextDisplayMode: starredDisplayMode,
      })
    ) {
      return;
    }

    if (
      !starredCount &&
      starredDisplayMode !== StarredDisplayModeEnum.FiltersApplied &&
      previousDisplayMode !== StarredDisplayModeEnum.FiltersApplied
    ) {
      return;
    }

    startServerFetch();
  }, [
    entity,
    fetchTableRowsAndGroupValues,
    starredDisplayMode,
    starredCount,
    fetchRides,
    t,
    startServerFetch,
  ]);

  return {
    anchorRef,
    closePopover,
    togglePopover,
    isPopoverOpen,
    starredCount,
    starredDisplayMode,
    handleButtonClick,
    handleItemClick,
    isDataReloading,
  };
};
